import { BAR_SLOTS, abilityNumber, blowOf, type BarRecord, type BarSlot, type Rules } from "odyssey-shared";
import { itemGlyph } from "../bag/describe.ts";
import type { BagSnapshot } from "../bag/store.ts";
import { barItemOf } from "./item-slot.ts";
import { abilitySlot, barSlots, emptySlot, type BarSlotSpec } from "./layout.ts";

/**
 * The bar's slots from the player's bar and the bag (P2.8-T29, ADR-121 decisions 1.2, 2.3 and 9). With no record the bar
 * is the default bar, derived at every draw as before: the rules' bar (`barSlots`) and, in the last slot, the first
 * consumable the bag holds (ADR-091 decision 14). A record names each slot's thing: a blow is the held weapon's (the
 * starting weapon's until equipment exists), an ability is read by its rules id, and an item is drawn with every unit
 * of it the bag carries and the record its use runs; an item at 0 stays, greyed (`spent`). A thing the running rules
 * lack, or an item that cannot be used from the bar, a tool among them (ADR-142 decision 3b), is drawn empty and kept in
 * the record. Keys stay by place.
 */

/**
 * The ten slots as the bar draws them.
 *
 * @param record The player's bar, or null for the default bar.
 * @param rules The page's rules.
 * @param bag The page's bag.
 * @returns The slots, in slot order.
 * @example
 * resolveBar(null, rules, bag).map((slot) => slot.name);
 * // ["Strike", "Heavy Strike", "Evasion", null, null, null, null, null, null, "Minor Healing Potion"]
 */
export function resolveBar(record: BarRecord | null, rules: Rules, bag: BagSnapshot): BarSlotSpec[] {
  if (record === null) {
    const slots = [...barSlots(rules)];
    const last = BAR_SLOTS - 1;
    const item = barItemOf(bag, rules);
    slots[last] = item === null ? emptySlot(last) : { ...emptySlot(last), ability: item.record, name: item.name, glyph: item.glyph, item: item.item, icon: item.icon, count: item.count };
    return slots;
  }
  return record.slots.map((slot, index) => slotOf(slot, index, rules, bag));
}

/**
 * The first bag slot holding an item, in slot order: the stack a press on the item's bar slot uses (ADR-121 decision
 * 9.3).
 *
 * @param bag The page's bag.
 * @param item The item's rules id.
 * @returns The bag slot, or null when the bag holds none.
 */
export function firstStackOf(bag: BagSnapshot, item: string): number | null {
  const slot = bag.slots.findIndex((held) => held.item === item && held.quantity > 0);
  return slot < 0 ? null : slot;
}

/**
 * One slot of a record as the bar draws it.
 *
 * @param slot What the record names there.
 * @param index The slot, from 0.
 * @param rules The page's rules.
 * @param bag The page's bag.
 * @returns The slot.
 */
function slotOf(slot: BarSlot, index: number, rules: Rules, bag: BagSnapshot): BarSlotSpec {
  if (slot === null) {
    return emptySlot(index);
  }
  if (slot[0] === "blow") {
    const ability = blowOf(rules, rules.startingWeapon, slot[1]);
    return ability === null ? emptySlot(index) : abilitySlot(rules, index, ability);
  }
  if (slot[0] === "ability") {
    const ability = rules.abilities.get(slot[1]);
    return ability === undefined ? emptySlot(index) : abilitySlot(rules, index, ability);
  }
  const item = rules.items.get(slot[1]);
  if (item === undefined || item.kind !== "consumable" || item.use === null || !rules.abilities.has(item.use)) {
    return emptySlot(index);
  }
  let count = 0;
  for (const held of bag.slots) {
    if (held.item === item.id) {
      count += held.quantity;
    }
  }
  return { ...emptySlot(index), ability: abilityNumber(rules, item.use), name: item.name, glyph: itemGlyph(rules, item.id), item: item.id, icon: item.icon, count, spent: count === 0 };
}
